"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { cfImage, rawImage } from "@/lib/img";

interface Props {
  photos: string[];
  title: string;
}

/**
 * One tour photo, requested at the size it's actually rendered.
 *
 * Before this, every tile/zoom/thumb requested the RAW R2 original — 2834×3543
 * (~10 MP, ~38 MB decoded, served as `application/octet-stream` so Cloudflare
 * marks it DYNAMIC and never edge-caches it). Twenty of those on a phone is
 * ~766 MB of decoded bitmap: iOS Safari drops requests (or kills the tab) and
 * the tile keeps its grey `bg-[#d9e0eb]` forever, because there was no onError.
 * That grey is the bug Maged photographed.
 *
 * So: ask Cloudflare for a resized variant (already proven in production on the
 * listing cards), and on failure fall back ONCE to the raw URL — transforms are
 * metered (5,000 unique/month free) and a 429 must not leave a blank tile.
 * `rawImage()` is a no-op on an already-raw URL, so the `raw !== src` guard
 * makes the swap happen exactly once and never loop.
 */
function TourImg({
  url,
  alt,
  width,
  eager = false,
  className,
  fit = "cover",
  style,
}: {
  url: string;
  alt: string;
  /** Rendered CSS width — cfImage applies its own DPR bump on top. */
  width: number;
  eager?: boolean;
  className: string;
  fit?: "cover" | "scale-down";
  style?: React.CSSProperties;
}) {
  // Callers that swap the photo in place (the zoom viewer) pass `key={zoom}`,
  // which remounts this component and re-initialises the fallback state — no
  // setState-in-effect needed.
  const [src, setSrc] = useState(() => cfImage(url, { width, fit }));
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onError={() => {
        const raw = rawImage(src);
        if (raw !== src) setSrc(raw);
      }}
      className={className}
      style={style}
    />
  );
}

/**
 * "Show all photos" → full-screen Photo Tour (BirdNest / Airbnb pattern).
 *
 * Self-managed full-screen layers — NOT the shared <Modal>, whose lightbox
 * variant inset the panel with black gutters and floated its own close button
 * as a clipped dark circle in the corner (Maged's "so buggy" screenshot).
 *
 *   GRID  (zoom === null): edge-to-edge white, sticky header with a close X,
 *          scrollable 1-big / 2-up mosaic so every photo is scannable at once.
 *   ZOOM  (zoom = number):  black single-photo viewer, arrows + swipe + a
 *          thumbnail strip, plus a "‹ All photos" button back to the grid.
 */
export default function PhotoLightbox({ photos, title }: Props) {
  const t = useTranslations("photoLightbox");
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState<number | null>(null);
  const swipeStart = useRef<number | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setZoom(null);
  }, []);
  const next = useCallback(
    () => setZoom((i) => (i === null ? i : Math.min(i + 1, photos.length - 1))),
    [photos.length]
  );
  const prev = useCallback(
    () => setZoom((i) => (i === null ? i : Math.max(i - 1, 0))),
    []
  );

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, [open]);

  // Keyboard: arrows page the zoom layer; Escape steps zoom→grid→closed.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (zoom !== null) {
        if (e.key === "ArrowRight") next();
        if (e.key === "ArrowLeft") prev();
        if (e.key === "Escape") setZoom(null);
      } else if (e.key === "Escape") {
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, zoom, next, prev, close]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setZoom(null);
          setOpen(true);
        }}
        className="absolute bottom-3.5 end-3.5 bg-white text-ss-text px-3.5 py-2 rounded-full text-[13px] font-semibold flex items-center gap-1.5 shadow-md hover:scale-105 transition-transform cursor-pointer z-10"
      >
        <GridIcon /> {t("showAllPhotos", { count: photos.length })}
      </button>

      {open && typeof document !== "undefined" && createPortal(
        zoom === null ? (
          /* ---------- GRID (Photo Tour) — edge-to-edge white ---------- */
          <div className="fixed inset-0 z-[250] bg-white overflow-y-auto overscroll-contain">
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-ss-line h-14 flex items-center gap-2 px-3 sm:px-5">
              <button
                type="button"
                onClick={close}
                aria-label={t("closePhotoTour")}
                className="w-9 h-9 -ms-1 rounded-full grid place-items-center hover:bg-ss-bg-soft text-ss-text"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
              <h2 className="h-display text-[17px] font-semibold">{t("photoTour")}</h2>
              <span className="text-[13px] text-ss-muted">{t("photosCount", { count: photos.length })}</span>
            </div>
            <div className="max-w-[920px] mx-auto p-2.5 sm:p-5">
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {photos.map((p, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setZoom(i)}
                    aria-label={t("openPhoto", { n: i + 1 })}
                    // Every 3rd tile (0, 3, 6 …) spans full width; the rest pair
                    // two-across — the 1-big / 2-up mosaic rhythm.
                    className={`relative overflow-hidden rounded-[12px] bg-[#d9e0eb] active:opacity-90 transition-opacity ${
                      i % 3 === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"
                    }`}
                  >
                    <TourImg
                      url={p}
                      alt={i === 0 ? title : t("openPhoto", { n: i + 1 })}
                      // Mosaic: full-span tiles are ~2× the width of the paired
                      // squares, so ask for proportionally sized variants.
                      width={i % 3 === 0 ? 720 : 380}
                      // Only the first tile is eager — the rest lazy-load as the
                      // guest scrolls, instead of 20 parallel origin fetches.
                      eager={i === 0}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ---------- ZOOM (single photo) ---------- */
          <div className="fixed inset-0 z-[260] bg-black/95 backdrop-blur flex flex-col">
            <div className="flex-1 flex items-center justify-center relative px-4 py-12 sm:px-20">
              <button
                type="button"
                onClick={() => setZoom(null)}
                aria-label={t("backToAllPhotos")}
                className="absolute top-4 start-4 sm:start-6 inline-flex items-center gap-1.5 bg-white/95 text-ss-text px-3 py-1.5 rounded-full text-[13px] font-semibold shadow-md z-10 hover:scale-105 transition-transform"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="rtl:-scale-x-100">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                {t("backToAllPhotos")}
              </button>
              <span className="absolute top-4 left-1/2 -translate-x-1/2 bg-ss-text/85 text-white px-3.5 py-1.5 rounded-full text-xs font-semibold backdrop-blur z-10">
                {t("photoCounter", { current: zoom + 1, total: photos.length })}
              </span>
              <button
                type="button"
                onClick={close}
                aria-label={t("close")}
                className="absolute top-4 end-4 w-9 h-9 rounded-full bg-white/90 text-ss-text grid place-items-center shadow-md z-10 hover:scale-105 transition-transform"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>

              <button
                type="button"
                onClick={prev}
                disabled={zoom === 0}
                aria-label={t("prevPhoto")}
                className="absolute start-2 sm:start-5 w-11 h-11 rounded-full bg-white/95 text-ss-text grid place-items-center text-xl shadow-md disabled:opacity-30 z-10 hover:scale-110 transition-transform"
              >
                <span className="rtl:-scale-x-100 inline-block">‹</span>
              </button>
              <div
                onTouchStart={(e) => (swipeStart.current = e.touches[0].clientX)}
                onTouchEnd={(e) => {
                  if (swipeStart.current === null) return;
                  const dx = e.changedTouches[0].clientX - swipeStart.current;
                  // TODO(rtl): swipe direction should invert under RTL (dx < 0 = advance in LTR = retreat in RTL).
                  // Requires reading dir from document or locale — logic change, deferred for QA.
                  if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
                  swipeStart.current = null;
                }}
                className="max-w-full max-h-full"
              >
                <TourImg
                  key={zoom}
                  url={photos[zoom]}
                  alt={t("viewPhoto", { n: zoom + 1 })}
                  // Full-bleed viewer: a 1400px scale-down variant is visually
                  // identical to the 2834px original on any phone/laptop.
                  width={1400}
                  fit="scale-down"
                  eager
                  className="max-w-full max-h-[calc(100dvh-180px)] object-contain rounded-[14px] shadow-[0_24px_80px_rgba(0,0,0,0.6)] fade-up"
                  style={{ animationDuration: "0.3s" }}
                />
              </div>
              <button
                type="button"
                onClick={next}
                disabled={zoom === photos.length - 1}
                aria-label={t("nextPhoto")}
                className="absolute end-2 sm:end-5 w-11 h-11 rounded-full bg-white/95 text-ss-text grid place-items-center text-xl shadow-md disabled:opacity-30 z-10 hover:scale-110 transition-transform"
              >
                <span className="rtl:-scale-x-100 inline-block">›</span>
              </button>
            </div>
            <div className="bg-black/60 backdrop-blur p-3.5 flex gap-1.5 overflow-x-auto scrollbar-none">
              {photos.map((p, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setZoom(i)}
                  aria-label={t("viewPhoto", { n: i + 1 })}
                  className={`flex-none rounded-lg overflow-hidden border-2 bg-white/10 transition-all ${
                    i === zoom
                      ? "border-white opacity-100"
                      : "border-transparent opacity-55 hover:opacity-90"
                  }`}
                  style={{ width: 88, height: 66 }}
                >
                  {/* 88×66 thumb — never the 10 MP original. */}
                  <TourImg url={p} alt="" width={96} className="w-full h-full object-cover block" />
                </button>
              ))}
            </div>
          </div>
        ),
        document.body,
      )}
    </>
  );
}

function GridIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}
